// @flow
import React from "react";
import styled, { keyframes, css } from "styled-components";
import tvgConf from "@tvg/conf";
import buildColor from "../ColorPalette";

const isDesktop = tvgConf().device === "desktop";

const pulseAnimation = keyframes`
  0% {
    fill: ${buildColor("blue", "000")};
  }

  50% {
    fill: ${buildColor("blue", "100")};
  }

  100% {
    fill: ${buildColor("blue", "000")};
  }
`;

const bottomPulseAnimation = keyframes`
  0% {
    fill: ${buildColor("blue", "100")};
  }

  50% {
    fill: ${buildColor("blue", "200")};
  }

  100% {
    fill: ${buildColor("blue", "100")};
  }
`;

const SVG = styled.svg`
  display: block;
  width: 100%;

  rect.pulse {
    animation: ${pulseAnimation} 1.25s infinite;
  }

  rect.bottomPulse {
    animation: ${bottomPulseAnimation} 1.25s infinite;
  }

  &.title,
  rect.title {
    height: 20px;
    width: 60%;
  }

  &.text,
  rect.text {
    width: 100%;
    height: 14px;
    margin-top: 4px;
    margin-bottom: 16px;
  }

  &.textDouble,
  rect.textDouble {
    width: 100%;
    height: ${isDesktop ? "17px" : "34px"};

    @media only screen and (min-width: 768px) {
      height: 17px;
    }
  }

  &.textDoubleMTP,
  rect.textDoubleMTP {
    width: 100%;
    height: 30px;

    @media only screen and (min-width: 768px) {
      height: 16px;
    }
  }

  &.mtpContainer,
  rect.mtpContainer {
    height: 35px;
    width: 100%;
    margin-bottom: 16px;
  }

  &.toggleContainer {
    height: 24px;
    width: 100%;
    padding-top: 24px;
    padding-bottom: 12px;
    display: flex;
    justify-content: ${isDesktop ? "flex-start" : "space-between"};
    ${!isDesktop &&
    css`
      box-shadow: inset 0px -1px 0px ${buildColor("blue", "100")};
    `};

    @media only screen and(min-width: 768px) {
      padding-top: 40px;
      padding-bottom: 20px;
    }
  }

  &.toggleSecondContainer {
    height: 24px;
    width: 100%;
    margin: 12px 0;
    display: flex;
    justify-content: space-between;

    @media only screen and (min-width: 768px) {
      margin: 20px 0;
    }
  }

  rect.toggleLeft {
    height: 16px;
    width: 25%;
    rx: 1;
    y: 4;
    ${isDesktop &&
    css`
      height: 24px;
      width: 24px;
      y: 0;
    `}
  }

  rect.toggleRight {
    height: 22px;
    width: 43px;
    rx: 12;
    y: 1;
    x: calc(100% - 43px);
    ${isDesktop &&
    css`
      height: 24px;
      width: 25%;
      rx: 1;
      x: 32;
    `}
  }

  &.muteAll,
  rect.muteAll {
    width: 100%;
    height: 32px;
  }
`;

const defaultContainer = css`
  width: 100%;
  background: ${buildColor("white", "100")};
  padding: ${isDesktop ? "20px" : "12px"};
  padding-bottom: 0;
  margin-bottom: 12px;
  ${!isDesktop &&
  css`
    box-shadow: inset 0px -1px 0px ${buildColor("blue", "100")};
  `};

  @media only screen and(min-width: 768px) {
    padding: 20px;
    padding-bottom: 0;
    border-radius: 4px;
  }
`;

const ContainerOneToggle = styled.div`
  ${defaultContainer};
  height: ${isDesktop ? "140px" : "126px"};

  @media only screen and (min-width: 768px) {
    height: ${isDesktop ? "140px" : "141px"};
  }
`;

const ContainerTwoToggles = styled.div`
  ${defaultContainer};
  height: 176px;

  @media only screen and (min-width: 768px) {
    height: 205px;
  }
`;

const ContainerMTP = styled.div`
  ${defaultContainer};
  height: 160px;

  @media only screen and (min-width: 768px) {
    height: 162px;
  }
`;

const ContainerMuteAll = styled.div`
  width: 100%;
  padding: 0 12px;
`;

const renderOneToggleBox = () => (
  <ContainerOneToggle>
    <SVG className="title">
      <rect className="pulse title" rx="1" />
    </SVG>
    <SVG className="textDouble">
      <rect className="pulse textDouble" rx="1" />
    </SVG>
    <SVG className="toggleContainer">
      <rect className="pulse toggleLeft" />
      <rect className="pulse toggleRight" />
    </SVG>
  </ContainerOneToggle>
);

const renderTwoTogglesBox = () => (
  <ContainerTwoToggles>
    <SVG className="title">
      <rect className="pulse title" rx="1" />
    </SVG>
    <SVG className="textDouble">
      <rect className="pulse textDouble" rx="1" />
    </SVG>
    <SVG className="toggleContainer">
      <rect className="pulse toggleLeft" />
      <rect className="pulse toggleRight" />
    </SVG>
    <SVG className="toggleSecondContainer">
      <rect className="pulse toggleLeft" />
      <rect className="pulse toggleRight" />
    </SVG>
  </ContainerTwoToggles>
);

const renderMTPBox = () => (
  <ContainerMTP>
    <SVG className="title">
      <rect className="pulse title" rx="1" />
    </SVG>
    <SVG className="text">
      <rect className="pulse text" rx="1" />
    </SVG>
    <SVG className="mtpContainer">
      <rect className="pulse mtpContainer" rx="4" />
    </SVG>
    <SVG className="textDoubleMTP">
      <rect className="pulse textDoubleMTP" rx="1" />
    </SVG>
  </ContainerMTP>
);

const renderMuteAll = () => (
  <ContainerMuteAll>
    <SVG className="muteAll">
      <rect className="bottomPulse muteAll" rx="2" />
    </SVG>
  </ContainerMuteAll>
);

const NotificationsPreferencesBodyMask = ({
  isNative
}: {
  isNative: boolean
}) =>
  isNative && !isDesktop ? (
    <div data-qa-label="notificationsPreferencesBodyMask">
      {renderMTPBox()}
      {renderTwoTogglesBox()}
      {renderTwoTogglesBox()}
      {renderOneToggleBox()}
      {renderTwoTogglesBox()}
      {renderMuteAll()}
    </div>
  ) : (
    <div data-qa-label="notificationsPreferencesBodyMask">
      {renderOneToggleBox()}
      {renderOneToggleBox()}
      {renderOneToggleBox()}
      {renderMuteAll()}
    </div>
  );

// $FlowFixMe
export default React.memo(NotificationsPreferencesBodyMask);
